import { gql } from "lib/apolloClient";
import {
  resolvedFieldValueFragment,
  toolResponseFragment,
  studentsAlertFragment,
  attachmentFragment,
} from "modules/CommonFragments";
import {
  entitySavedByNodeFragment,
  libraryFragments,
} from "ToddleLibrary/modules/ToddleLibraryFragments";
import {
  organizationResourceParentFragment,
  questionSubmissionFragments,
  assessmentToolFragments,
  questionFragments,
  organizationResourceQuestionFragment,
  organizationResourceParentFragmentV2,
  studentAssigmentSubmissionWithEvaluationV2,
  assignedStudentFieldsFragment,
  libraryQuestionFragment,
  studentDetailsFragment,
  aiMessageFragment,
} from "./WorksheetFragments";
import {
  entityEventTrackFragment,
  studentAssigmentSubmissionFragment,
} from "ClassRoom/modules/ClassRoomFragments";

/**
 * @deprecated use getAssessmentQuestionsQueryV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getAssessmentQuestionsQuery = gql`
  query getAssessmentQuestions(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orgResourceFilters: OrganizationResourceFilter
  ) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        organizationResourcesV2(first: null, filters: $orgResourceFilters) {
          edges {
            node {
              ...organizationResourceParentItem
            }
          }
        }

        libraryGrades {
          ...libraryEntityBasicDetailsItem
        }
        librarySubjects {
          ...libraryEntityBasicDetailsItem
        }

        isLibraryPremium
        showLibraryPremiumBannerForUser
        ...assessmentSavedByNodeItem
      }
    }
  }
  ${entitySavedByNodeFragment.assessment}
  ${organizationResourceParentFragment}
  ${libraryFragments.libraryEntityBasicDetails}
  ${resolvedFieldValueFragment.resolvedMinimalTree}
`;

export const getStudentAssignmentQuestionSubmissionsQuery = gql`
  query getStudentAssignmentQuestionSubmissions(
    $submissionId: ID!
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
  ) {
    node(id: $submissionId, type: STUDENT_ASSIGNMENT_SUBMISSION) {
      id
      ... on StudentAssignmentSubmission {
        id
        status
        questionSubmissions {
          totalCount
          totalQuestionAttempted
          edges {
            node {
              ...questionSubmissionItem
            }
          }
        }
      }
    }
  }
  ${questionSubmissionFragments.questionSubmission}
`;

export const getAssessmentToolsByQuestionSubmissionIdQuery = gql`
  # Query to fetch all assessment tools details ( used in new theatre mode )
  query getAssessmentToolsByQuestionSubmissionId($id: ID!) {
    node(id: $id, type: QUESTION_SUBMISSION) {
      id
      ... on QuestionSubmission {
        id
        question {
          id
          assessmentTools {
            totalCount
            edges {
              node {
                ...questionAssessmentToolItem
              }
            }
          }
        }
      }
    }
  }
  ${assessmentToolFragments.questionAssessmentTool}
`;

export const getQuestionTopicsQuery = gql`
  query getQuestionTopics($input: QuestionTagInput) {
    content {
      getQuestionTags(input: $input) {
        id
        label
        type
      }
    }
  }
`;

export const getSyllabusContentTopicsQuery = gql`
  query getSyllabusContentTopics($id: ID!) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        associatedChildPlannerElementNodes(filters: { types: DP_SYLLABUS }) {
          id
          displaySequence
          label
          depth
          parent
        }
      }
    }
  }
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades(
    $organizationId: ID!
    $filters: GradeFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          globalGrade {
            id
            uid
          }
        }
      }
    }
  }
`;

export const getOrganizationSubjectsQuery = gql`
  query getOrganizationSubjects(
    $organizationId: ID!
    $filters: SubjectFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $filters) {
          id
          name
          globalSubject
          subjectGroup {
            id
            name
          }
        }
      }
    }
  }
`;

export const getSchoolQuestionLibraryFeedQuery = gql`
  query getSchoolQuestionLibraryFeed(
    $orgResourceFilters: OrganizationResourceFilter
    $first: Int
    $after: String
    $orgId: ID!
    $orderBy: ORDER_BY_ENUM
  ) {
    node(type: ORGANIZATION, id: $orgId) {
      id
      ... on Organization {
        id
        organizationResourcesV2(
          filters: $orgResourceFilters
          first: $first
          after: $after
          orderBy: $orderBy
        ) {
          resourceTypeBasedCount {
            totalCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              item {
                ...questionLibraryFeedDetailsItem
              }
            }
          }
        }
      }
    }
  }
  ${questionFragments.questionLibraryFeedDetails}
`;
export const getSchoolQuestionLibraryFeedQueryV2 = gql`
  query getQuestionLibraryFeedV2(
    $questionFilters: OrganizationResourceQuestionItemFilters
    $first: Int
    $after: String
    $orgId: ID!
    $orderBy: ORDER_BY_ENUM
  ) {
    node(type: ORGANIZATION, id: $orgId) {
      id
      ... on Organization {
        id
        questions(
          filters: $questionFilters
          first: $first
          after: $after
          orderBy: $orderBy
        ) {
          edges {
            ...questionLibraryFeedDetailsItem
          }
          count {
            totalCount
            questionStatusWiseCount {
              status
              count
            }
          }
          pageInfo {
            hasNextPage
            endCursor
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
  ${questionFragments.questionLibraryFeedDetailsV2}
`;

/**
 * @deprecated use getToddleQuestionLibraryFeedQueryV2 instead
 */
export const getToddleQuestionLibraryFeedQuery = gql`
  query getToddleQuestionLibraryFeed(
    $id: ID!
    $entityTypes: [LIBRARY_ENTITY_TYPE_ENUM!]
    $searchText: String
    $first: Int
    $after: String
    $filters: LibraryEntityFilter
    $seed: Float!
    $sortBy: LIBRARY_SORT_ENUM
    $questionFilters: OrganizationResourceQuestionItemFilters
    $feedType: LIBRARY_FEED_ENUM
    $showOnlyAccessible: Boolean
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryEntities(
          entityTypes: $entityTypes
          first: $first
          after: $after
          searchText: $searchText
          filters: $filters
          seed: $seed
          sortBy: $sortBy
          questionFilters: $questionFilters
          feedType: $feedType
          showOnlyAccessible: $showOnlyAccessible
        ) {
          totalCount
          edges {
            node {
              id
              ... on OrganizationResource {
                id
                item {
                  ...questionLibraryFeedDetailsItem
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${questionFragments.questionLibraryFeedDetails}
`;

export const getQuestionLibraryQuestionQuery = gql`
  query getQuestionLibraryQuestion($id: ID!) {
    node(id: $id, type: ORGANIZATION_RESOURCE) {
      id
      ... on OrganizationResource {
        ...organizationResourceQuestionItem
      }
    }
  }
  ${organizationResourceQuestionFragment}
`;

export const getAIConversationsQuery = gql`
  query getAiConversation($id: ID!) {
    node(id: $id, type: AI_CONVERSATION) {
      ... on AiConversation {
        id
        entityType
        metadata
        messages {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...aiMessageItem
            }
          }
        }
        attachments {
          ...attachmentItem
        }
      }
    }
  }
  ${aiMessageFragment.aiMessage}
  ${attachmentFragment.attachment}
`;

/**
 * @deprecated Use `getWorksheetQuestionWiseInsightsV2Query` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const getWorksheetQuestionWiseInsightsQuery = gql`
  query getWorksheetQuestionWiseInsights($assignmentId: ID!) {
    node(id: $assignmentId, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        content {
          ... on Assessment {
            id
            organizationResources {
              id
              sections: childrenV2(
                first: null
                orderBy: DISPLAY_SEQUENCE
                orderByDirection: ASC
              ) {
                edges {
                  node {
                    id
                    label
                    questions: childrenV2(
                      first: null
                      orderBy: DISPLAY_SEQUENCE
                      orderByDirection: ASC
                    ) {
                      resourceTypeBasedCount {
                        totalCount
                      }
                      edges {
                        node {
                          id
                          item {
                            ...questionInsightsDetailsItem
                          }
                          subQuestions: childrenV2(
                            first: null
                            orderBy: DISPLAY_SEQUENCE
                            orderByDirection: ASC
                          ) {
                            resourceTypeBasedCount {
                              totalCount
                            }
                            edges {
                              node {
                                id
                                item {
                                  ...questionInsightsDetailsItem
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        assignedStudents {
          id
          student {
            ...studentItem
            ... on Student {
              id
              alertTypes {
                ...alertTypeFields
              }
            }
          }
          status
          isSubmitted
          isEvaluated
          isSharedWithStudent
          isEvaluationApplicable
          toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
            id
            toolId
            toolType
            toolResponses {
              ...toolResponseItem
            }
          }
          ibPYPElementRatings {
            id
            value
            ibPYPElementId
            ibPYPElementType
            academicCriteriaValue {
              id
              label
            }
            academicCriteriaSet {
              id
            }
          }
          submission {
            id
            questionSubmissions {
              edges {
                node {
                  ...questionSubmissionInsightsDetailsItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${questionFragments.questionInsightsDetails}
  ${studentDetailsFragment}
  ${questionSubmissionFragments.questionSubmissionInsightsDetails}
  ${toolResponseFragment.toolResponse}
  ${studentsAlertFragment}
`;

/**
 * @deprecated Use `getWorksheetScoreDistributionQueryV2` instead.
 *  This is kept for backward compatibility.
 *  It will be removed in future versions.
 */
export const getWorksheetScoreDistributionQuery = gql`
  query getWorksheetScoreDistribution($assignmentId: ID!, $courseIds: [ID!]) {
    node(id: $assignmentId, type: ASSIGNMENT) {
      ... on Assignment {
        id
        state {
          state
        }
        content {
          ... on Assessment {
            id
            academicTerm {
              id
            }
            fields(
              uids: [
                "measureAssessing"
                "objectivesMYP"
                "interdisciplinaryCriteriaMYP"
              ]
            ) {
              id
              uid
              value
              resolvedMinimalTree {
                ...resolvedMinimalTreeItem
              }
            }
            assessmentTools {
              edges {
                node {
                  id
                  ...questionAssessmentToolItem
                }
              }
            }
            organizationResources {
              id
              sections: childrenV2(
                first: null
                orderBy: DISPLAY_SEQUENCE
                orderByDirection: ASC
              ) {
                edges {
                  node {
                    id
                    label
                    questions: childrenV2(
                      first: null
                      orderBy: DISPLAY_SEQUENCE
                      orderByDirection: ASC
                    ) {
                      resourceTypeBasedCount {
                        totalCount
                      }
                      edges {
                        node {
                          id
                          item {
                            ...questionScoreAndToolDetailsItem
                          }
                          subQuestions: childrenV2(
                            first: null
                            orderBy: DISPLAY_SEQUENCE
                            orderByDirection: ASC
                          ) {
                            resourceTypeBasedCount {
                              totalCount
                            }
                            edges {
                              node {
                                id
                                item {
                                  ...questionScoreAndToolDetailsItem
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        assignedStudents {
          id
          status
          isSubmitted
          isEvaluated
          isSharedWithStudent
          isEvaluationApplicable
          student {
            ...studentItem
            courseMap(filters: { courseIds: $courseIds }) {
              tags {
                id
                label
              }
            }
            alertTypes {
              ...alertTypeFields
            }
          }
          toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
            id
            toolId
            toolType
            toolResponses {
              ...toolResponseItem
            }
          }
          ibPYPElementRatings {
            id
            value
            ibPYPElementId
            ibPYPElementType
            academicCriteriaValue {
              id
              label
            }
            academicCriteriaSet {
              id
            }
          }
          elementRemarks {
            id
            message
            ibPYPElementId
            ibPYPElementType
          }
          submission {
            id
            questionSubmissions {
              edges {
                node {
                  ...questionSubmissionEvaluationDetailsItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${resolvedFieldValueFragment.resolvedMinimalTree}
  ${assessmentToolFragments.questionAssessmentTool}
  ${questionFragments.questionScoreAndToolDetails}
  ${questionSubmissionFragments.questionSubmissionEvaluationDetails}
  ${studentsAlertFragment}
  ${studentDetailsFragment}
`;
export const getWorksheetScoreDistributionQueryV2 = gql`
  query getWorksheetScoreDistribution($assignmentId: ID!, $courseIds: [ID!]) {
    node(id: $assignmentId, type: ASSIGNMENT) {
      ... on Assignment {
        id
        state {
          state
        }
        content {
          ... on Assessment {
            id
            academicTerm {
              id
            }
            fields(
              uids: [
                "measureAssessing"
                "objectivesMYP"
                "interdisciplinaryCriteriaMYP"
              ]
            ) {
              id
              uid
              value
              resolvedMinimalTree {
                ...resolvedMinimalTreeItem
              }
            }
            assessmentTools {
              edges {
                node {
                  id
                  ...questionAssessmentToolItem
                }
              }
            }
            questions {
              totalCount
              edges {
                ...questionItem
              }
            }
          }
        }
        assignedStudents {
          id
          status
          isSubmitted
          isEvaluated
          isSharedWithStudent
          isEvaluationApplicable
          student {
            ...studentItem
            courseMap(filters: { courseIds: $courseIds }) {
              tags {
                id
                label
              }
            }
            alertTypes {
              ...alertTypeFields
            }
          }
          toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
            id
            toolId
            toolType
            toolResponses {
              ...toolResponseItem
            }
          }
          ibPYPElementRatings {
            id
            value
            ibPYPElementId
            ibPYPElementType
            academicCriteriaValue {
              id
              label
            }
            academicCriteriaSet {
              id
            }
          }
          elementRemarks {
            id
            message
            ibPYPElementId
            ibPYPElementType
          }
          submission {
            id
            questionSubmissions {
              edges {
                node {
                  ...questionSubmissionEvaluationDetailsItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${resolvedFieldValueFragment.resolvedMinimalTree}
  ${assessmentToolFragments.questionAssessmentTool}
  ${questionSubmissionFragments.questionSubmissionEvaluationDetails}
  ${studentsAlertFragment}
  ${questionFragments.questionV2}
  ${studentDetailsFragment}
`;

/**
 * @deprecated use getStudentQuestionDetailsQueryV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getStudentQuestionDetailsQuery = gql`
  query getStudentQuestionDetails(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orgResourceFilters: OrganizationResourceFilter
    $showAssessmentTools: Boolean = false
  ) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        isDeleted
        title {
          id
          uid
          value
        }
        description {
          id
          uid
          value
        }
        organizationResourcesV2(first: null, filters: $orgResourceFilters) {
          edges {
            node {
              ...organizationResourceParentItem
            }
          }
        }
      }
    }
  }
  ${organizationResourceParentFragmentV2}
`;

export const getStudentAssignmentDetailsQueryV2 = gql`
  query getStudentAssignmentDetailsV2(
    $id: ID!
    $showOnlySubmitted: Boolean
    $entityEventsFilter: EntityEventsFilter
  ) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        id
        isSubmitted
        isEvaluated
        isSharedWithStudent
        isEvaluationApplicable
        assignment {
          id
          contentType
          isLockScreenEnabled
          isSafeBrowserEnabled
          isTimedTestEnabled
          timedTestDuration
          isStudentEvaluationEnabled
          isStudentSubmissionEnabled
          showStudentResponse
          isOnScreenCalculatorEnabled
          closeSubmissionDate
          content {
            ... on Assessment {
              id
              templateId
              assessmentTools {
                edges {
                  node {
                    id
                    assessmentToolType
                  }
                }
              }
            }
          }
          course {
            id
            genericTags {
              id
              label
            }
          }
        }
        lockingState {
          state
        }
        ibPYPElementRatings {
          id
          value
          ibPYPElementType
        }
        events(filters: $entityEventsFilter) {
          ...entityEventTrackItem
        }
        submission(showOnlySubmitted: $showOnlySubmitted) {
          ...studentAssigmentSubmissionItem
        }
      }
    }
  }
  ${entityEventTrackFragment.entityEventTrack}
  ${studentAssigmentSubmissionFragment.studentAssigmentSubmission}
`;

export const getStudentAssignmentQueryV2 = gql`
  query getStudentAssignmentV2(
    $id: ID!
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
    $entityEventsFilter: EntityEventsFilter
  ) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        id
        isSubmitted
        isEvaluationApplicable
        isSharedWithStudent
        toolResponses: toolResponsesV2(filters: { userFilter: $userFilter }) {
          id
          toolId
          toolType
          toolResponses {
            ...toolResponseItem
          }
        }
        assignment {
          id
          contentType
          isLockScreenEnabled
          isSafeBrowserEnabled
          isTimedTestEnabled
          timedTestDuration
          isTeacherEvaluationEnabled
          isStudentEvaluationEnabled
          isTeacherEvaluationEnabled
          isStudentSubmissionEnabled
          showStudentResponse
          isOnScreenCalculatorEnabled
          closeSubmissionDate
          isFixedStartTime
          publishedAt
          content {
            ... on Assessment {
              id
              templateId
              allFields {
                id
                uid
                value
                resolvedMinimalTree {
                  ...resolvedMinimalTreeItem
                }
              }
              assessmentTools {
                edges {
                  node {
                    ...questionAssessmentToolItem
                  }
                }
              }
            }
          }
          course {
            id
            genericTags {
              id
              label
            }
          }
        }
        lockingState {
          state
        }
        ibPYPElementRatings {
          id
          value
          ibPYPElementType
        }
        events(filters: $entityEventsFilter) {
          ...entityEventTrackItem
        }
        submissions {
          edges {
            ...studentAssigmentSubmissionItem
          }
        }
        conversation {
          id
          unreadMessageCount
          unreadMessageCounts {
            itemTypeV2
            itemType
            count
          }
        }
      }
    }
  }
  ${entityEventTrackFragment.entityEventTrack}
  ${studentAssigmentSubmissionWithEvaluationV2}
  ${resolvedFieldValueFragment.resolvedMinimalTree}
  ${assessmentToolFragments.questionAssessmentTool}
  ${toolResponseFragment.toolResponse}
`;

export const getSignedUrlsQuery = gql`
  query GetSignedUrl($input: SignedUrlsInput!) {
    content {
      getSignedUrl(input: $input) {
        signedUrls {
          originalUrl
          signedUrl
        }
        isSuccess
        warning
      }
    }
  }
`;

export const getAssessmentQuestionsQueryV2 = gql`
  query getAssessmentQuestionsV2($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        questions {
          totalCount
          edges {
            ...questionItem
          }
        }
      }
    }
  }
  ${questionFragments.questionV2}
`;

export const getStudentQuestionDetailsQueryV2 = gql`
  query getStudentQuestionDetailsV2($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        isDeleted
        title {
          id
          uid
          value
        }
        description {
          id
          uid
          value
        }
        questions {
          totalCount
          edges {
            ...questionItem
          }
        }
      }
    }
  }
  ${questionFragments.studentQuestionDetailsV2}
`;

export const getWorksheetQuestionWiseInsightsV2Query = gql`
  query getWorksheetQuestionWiseInsightsV2($assignmentId: ID!) {
    node(id: $assignmentId, type: ASSIGNMENT) {
      id
      ... on Assignment {
        content {
          ... on Assessment {
            id
            questions {
              edges {
                ...questionInsightsDetailsItem
              }
            }
          }
        }
        assignedStudents {
          ...assignedStudentItem
        }
      }
    }
  }
  ${questionFragments.questionInsightsDetailsV2}
  ${assignedStudentFieldsFragment}
`;

export const getQuestionLibraryQuestionQueryV2 = gql`
  query getQuestionLibraryQuestionV2($id: ID!) {
    node(id: $id, type: QUESTION) {
      id
      ... on Question {
        ...libraryQuestionItem
      }
    }
  }
  ${libraryQuestionFragment}
`;

export const getWorksheetDownloadJobCountQuery = gql`
  query getWorksheetDownloadJobCount(
    $id: ID!
    $filters: OrganizationIntegrationJobFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        jobFeed(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getToddleQuestionLibraryFeedQueryV2 = gql`
  query getToddleQuestionLibraryFeedV2(
    $id: ID!
    $entityTypes: [LIBRARY_ENTITY_TYPE_ENUM!]
    $searchText: String
    $first: Int
    $after: String
    $filters: LibraryEntityFilter
    $seed: Float!
    $sortBy: LIBRARY_SORT_ENUM
    $questionFilters: OrganizationResourceQuestionItemFilters
    $feedType: LIBRARY_FEED_ENUM
    $showOnlyAccessible: Boolean
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryEntities(
          entityTypes: $entityTypes
          first: $first
          after: $after
          searchText: $searchText
          filters: $filters
          seed: $seed
          sortBy: $sortBy
          questionFilters: $questionFilters
          feedType: $feedType
          showOnlyAccessible: $showOnlyAccessible
        ) {
          totalCount
          edges {
            node {
              id
              ... on Question {
                ...questionLibraryFeedDetailsItem
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${questionFragments.questionLibraryFeedDetailsV2}
`;
